import { formatCurrency } from "@/lib/utils";
import { PaymentTypeLabel } from "@bidbattles/bloks/components/payments";
import { DataTable } from "@bidbattles/bloks/components/ui/table";
import {
	BBBackendQueryOptions,
	type LoyaltyPointsTransactionHistoryItem,
} from "@bidbattles/bloks/lib/rapace";
import { paginationSearchParamsSchema } from "@bidbattles/bloks/lib/rapace/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ClientOnly } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";
import { CoinsSwap01, User01 } from "untitledui-js/react";

export const Route = createFileRoute("/settings/earnings")({
	loaderDeps: ({ search }) => ({ pagination: search }),
	async loader({ context, deps: { pagination } }) {
		await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.listLoyaltyPointsTransactionHistory(pagination),
		);
	},
	validateSearch: zodValidator(paginationSearchParamsSchema),
	component: RouteComponent,
	ssr: false,
});

const columnHelper = createColumnHelper<LoyaltyPointsTransactionHistoryItem>();

function RouteComponent() {
	const paginationSearchParams = Route.useSearch();
	const {
		data: { history, pagination },
	} = useSuspenseQuery(
		BBBackendQueryOptions.listLoyaltyPointsTransactionHistory(
			paginationSearchParams,
		),
	);

	const columns = useMemo(
		() => [
			columnHelper.accessor("type", {
				header: "Type",
				cell: (info) => {
					return (
						<span className="text-gray-500 inline-flex items-center gap-2">
							<PaymentTypeLabel
								type={info.getValue()}
								amount={info.row.original.amount}
							/>
						</span>
					);
				},
			}),

			columnHelper.accessor("amount", {
				header: "Amount",
				cell: (info) => {
					const multiplier =
						info.row.original.type === "points_to_bids_conversion" ? -1 : 1;
					return formatCurrency("LOYALTY_POINTS", multiplier * info.getValue());
				},
			}),

			columnHelper.accessor("name", {
				header: "Reference",
				cell: (info) => {
					return info.getValue();
				},
			}),

			columnHelper.accessor("created_at", {
				header: () => (
					<span className="text-end w-full inline-block">Date</span>
				),
				cell: (info) => {
					const created_at = info.getValue();
					return (
						<span className="text-gray-500 text-end w-full inline-block">
							{created_at.toLocaleString()}
						</span>
					);
				},
			}),
		],
		[],
	);

	return (
		<>
			<DataTable
				hideFooters
				columns={columns}
				data={history}
				pagination={pagination}
			/>
		</>
	);
}
